import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import SEOProvider from './components/SEOProvider';
import Header from './components/Header';
import Footer from './components/Footer';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Register from './pages/Register';
import Listings from './pages/Listings';
import ListingDetail from './pages/ListingDetail';
import TransactionDetail from './pages/BorrowingBookingDetails';
import BookingsPage from './pages/BorrowingBookings';
import LendingBookings from './pages/LendingBookings';
import LendingBookingDetail from './pages/LendingBookingDetails';
import Profile from './pages/Profile';
import AboutUs from './pages/AboutUs';
import MyBookings from './pages/MyBookings';
import MyLendingBookings from './pages/MyLendingBookings';
import MyListings from './pages/MyListings';
import MyListing from './pages/MyListing';
import CreateListing from './pages/CreateListing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CommunityGuidelines from './pages/CommunityGuidelinesPage';
import FAQ from './pages/FAQ';
import SuccessPage from './pages/SuccessPage';
import CancelPage from './pages/CancelPage';
import StripeReturn from './pages/StripeReturn';
import StripeRefresh from './pages/StripeRefresh';
import CompletedBookings from './pages/CompletedBookings';
import LendingCompletedBookings from './pages/LendingCompletedBookings';
import LendingOutdatedBookings from './pages/LendingOutdatedBookings';
import OutdatedBookings from './pages/OutdatedBookings';
import PasswordRecovery from './pages/PasswordRecovery';
import ResetPassword from './pages/ResetPassword';
import VerificationSuccess from './pages/VerificationSuccess';
import PasswordResetSuccess from './pages/PasswordResetSuccess';
import BorrowerReviewPage from './pages/BorrowerReviewPage';
import Wishlist from './pages/Wishlist';
import Conversations from './pages/Conversations';
import OAuthCallback from './pages/OAuthCallback';
import ProfileMenu from './pages/ProfileMenu';
import VerifyAccount from './pages/VerifyAccount';
import Terms from './pages/Terms';
import MobileBottomNavigation from './components/MobileBottomNavigation';
import UserProfile from './pages/UserProfile';
import HowItWorks from './pages/HowItWorksPage';
import HomePage from './pages/HomePage';
import theme from './theme';
import { ModeProvider } from './context/ModeContext';

// Stripe integration
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <Elements stripe={stripePromise}>
        <AuthProvider>
          <ModeProvider>
            <ThemeProvider theme={theme}>
              <SEOProvider>
                <CssBaseline />
                <div className="App" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                  <Header />
                  <ScrollToTop />
                  <main style={{ flexGrow: 1 }}>
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/create-listing" element={<CreateListing />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/listings" element={<Listings />} />
                      <Route path="/listings/:uuid" element={<ListingDetail />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/about" element={<AboutUs />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/transactions/:id" element={<TransactionDetail />} />
                      <Route path="/bookings" element={<BookingsPage />} />
                      <Route path="/current-bookings" element={<MyBookings />} />
                      <Route path="/completed-bookings" element={<CompletedBookings />} />
                      <Route path="/outdated-bookings" element={<OutdatedBookings />} />
                      <Route path="/my-lending-bookings" element={<MyLendingBookings />} />
                      <Route path="/my-listings" element={<MyListings />} />
                      <Route path="/my-listings/:id" element={<MyListing />} />
                      <Route path="/lending-bookings" element={<LendingBookings />} />
                      <Route path="/lending-bookings/:id" element={<LendingBookingDetail />} />
                      <Route path="/lending-completed-bookings" element={<LendingCompletedBookings />} />
                      <Route path="/lending-outdated-bookings" element={<LendingOutdatedBookings />} />
                      <Route path="/success" element={<SuccessPage />} />
                      <Route path="/cancel" element={<CancelPage />} />
                      <Route path="/return" element={<StripeReturn />} />
                      <Route path="/refresh" element={<StripeRefresh />} />
                      <Route path="/users/:userId" element={<UserProfile />} />
                      <Route path="/password-recovery" element={<PasswordRecovery />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/wishlist" element={<Wishlist />} />
                      <Route path="/conversations" element={<Conversations />} />
                      <Route path="/profile-menu" element={<ProfileMenu />} />
                      <Route path="/verify-success" element={<VerificationSuccess />} />
                      <Route path="/password-reset-success" element={<PasswordResetSuccess />} />
                      <Route path="/verify/:token" element={<VerifyAccount />} />
                      <Route path="/oauth/callback" element={<OAuthCallback />} />
                      <Route path="/lender/review/borrower/:transaction_id" element={<BorrowerReviewPage />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/community-guidelines" element={<CommunityGuidelines />} />
                      <Route path="/how-it-works" element={<HowItWorks />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </main>
                  <Footer />
                  <MobileBottomNavigation />
                </div>
              </SEOProvider>
            </ThemeProvider>
          </ModeProvider>
        </AuthProvider>
      </Elements>
    </HelmetProvider>
  );
}

export default App;